import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { ChevronDown, LucideAngularModule, Search, X, XCircle } from 'lucide-angular';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { Observable, takeUntil } from 'rxjs';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-select',
  imports: [ReactiveFormsModule, NgClass, LucideAngularModule, FormsModule, ButtonComponent],
  templateUrl: './select.component.html',
  styleUrl: './select.component.css',
  standalone: true,
})
export class SelectComponent extends Destroyer implements OnInit, OnChanges {
  @Input() control?: FormControl = undefined;
  @Input() choices!: { value: any; label: string }[] | Observable<{ value: any; label: string }[]>;
  @Input() label!: string;
  @Input() placeholder?: string;
  @Input() multiple = false;
  @Input() height = '11';
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search...';

  @Input() allowCreate = false;
  @Input() createPlaceholder = '';
  @Output() optionCreate = new EventEmitter<{ name: string }>();

  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly XIcon = X;
  protected readonly XCircleIcon = XCircle;
  protected readonly SearchIcon = Search;
  protected loading = true;

  options: {
    id: number;
    value: any;
    label: string;
    selected: boolean;
  }[] = [];

  allOptions: {
    id: number;
    value: any;
    label: string;
    selected: boolean;
  }[] = [];

  isOpen = false;
  private clickListener: (() => void) | null = null;

  newOption: string = '';
  creating = false;
  searchQuery: string = '';

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
  ) {
    super();
  }

  ngOnInit() {
    // listen for control reset and reset the selected options
    if (this.control) {
      this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.options.forEach((option) => {
          option.selected = this.isSelectedInControl(option.value);
        });
        this.allOptions.forEach((option) => {
          option.selected = this.isSelectedInControl(option.value);
        });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['choices']) {
      this.loading = false;
      this.newOption = '';
      this.searchQuery = '';
      if (this.choices instanceof Observable) {
        this.loading = true;
        this.choices.pipe(takeUntil(this.destroy$)).subscribe((choices) => {
          this.setOptions(choices);
          this.loading = false;
        });
      } else {
        this.loading = false;
        this.setOptions(this.choices);
      }
    }
  }

  private setOptions(choices: { value: any; label: string }[]) {
    this.allOptions = choices.map((choice) => {
      return {
        value: choice.value,
        label: choice.label,
        selected: this.isSelectedInControl(choice.value),
        id: Math.floor(Math.random() * 1000000),
      };
    });
    this.options = [...this.allOptions];
  }

  toggleOpen() {
    if (this.loading) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickListener();
      // Reset search when opening
      this.searchQuery = '';
      this.filterOptions();
    } else {
      this.removeClickListener();
    }
  }

  select(id: number) {
    const option = this.allOptions.find((opt) => opt.id === id);
    if (option) {
      if (this.multiple) {
        option.selected = !option.selected;
      } else {
        this.allOptions.forEach((opt) => {
          opt.selected = false;
        });
        option.selected = true;
      }
      // Update the filtered options as well
      this.options.forEach((opt) => {
        if (opt.id === id) {
          opt.selected = option.selected;
        } else if (!this.multiple) {
          opt.selected = false;
        }
      });
    }
    if (!this.multiple) {
      this.toggleOpen();
    }

    this.control?.setValue(this.selectedValues());
    this.control?.markAsDirty();
  }

  deselect(id: number) {
    const optionAll = this.allOptions.find((opt) => opt.id === id);
    if (optionAll) {
      optionAll.selected = false;
    }
    const optionFiltered = this.options.find((opt) => opt.id === id);
    if (optionFiltered) {
      optionFiltered.selected = false;
    }
    this.control?.setValue(this.selectedValues());
    this.control?.markAsDirty();
  }

  clearAll() {
    // Prevent event propagation to avoid opening dropdown
    event?.stopPropagation();

    // Deselect all options
    this.allOptions.forEach((opt) => (opt.selected = false));
    this.options.forEach((opt) => (opt.selected = false));

    // Update control value
    if (this.multiple) {
      this.control?.setValue([]);
    } else {
      this.control?.setValue(null);
    }
    this.control?.markAsDirty();
  }

  hasSelectedOptions(): boolean {
    return this.selectedOptions().length > 0;
  }

  selectedValues() {
    if (this.multiple) {
      return this.selectedOptions().map((option) => option.value);
    } else {
      return this.selectedOptions().map((option) => option.value)[0];
    }
  }

  selectedOptions() {
    return this.allOptions.filter((option) => option.selected);
  }

  private addClickListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (!this.elRef.nativeElement.contains(event.target)) {
        this.toggleOpen();
      }
    });
  }

  private removeClickListener() {
    if (this.clickListener) {
      this.clickListener();
      this.clickListener = null;
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.removeClickListener();
  }

  private isSelectedInControl(value: any) {
    if (!this.control || !this.control.value) {
      return false;
    }
    if (this.multiple) {
      return this.control.value.includes(value);
    }
    return this.control.value === value;
  }

  createOption() {
    this.creating = true;
    this.optionCreate.emit({ name: this.newOption });
  }

  onSearchChange() {
    this.filterOptions();
  }

  filterOptions() {
    if (!this.searchQuery) {
      this.options = [...this.allOptions];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.options = this.allOptions.filter((option) => option.label.toLowerCase().includes(query));
  }

  protected readonly Validators = Validators;
}
